/*
 * VPDB - Virtual Pinball Database
 * Copyright (C) 2019 freezy <freezy@vpdb.io>
 *
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; either version 2
 * of the License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 * Modified for sigmashake-pinball by SigmaShake, 2026.
 */

import type { ItemFrame } from "../../../shared/contracts";
import { EventProxy } from "../../core/event-proxy";
import type { IAnimatable, IAnimation } from "../../core/ianimatable";
import type { IHittable } from "../../core/ihittable";
import { Item } from "../../core/item";
import type { Player } from "../../core/player";
import { f4 } from "../../math/float";
import type { HitObject } from "../../physics/hit-object";
import type { Table } from "../../table-model";
import type { Ball } from "../ball/ball";
import { HitTargetAnimation } from "./hit-target-animation";
import type { HitTargetData } from "./hit-target-data";
import { HitTargetHitGenerator } from "./hit-target-hit-generator";
import { HitTargetState } from "./hit-target-state";

/**
 * A standup target or a drop target.
 */
export class HitTarget extends Item<HitTargetData> implements IHittable, IAnimatable {
  public static DROP_TARGET_LIMIT = f4(52.0);

  private readonly state: HitTargetState;
  private readonly hitGenerator: HitTargetHitGenerator;
  private animation?: HitTargetAnimation;
  private hits?: HitObject[];

  public constructor(data: HitTargetData) {
    super(data);
    this.state = new HitTargetState(this.data.getName(), data.isDropTarget(), data.isVisible);
    this.hitGenerator = new HitTargetHitGenerator(data);
    if (data.isDropTarget() && data.isDropped) {
      this.state.zOffset = -HitTarget.DROP_TARGET_LIMIT;
    }
  }

  public isCollidable(): boolean {
    return this.data.isCollidable;
  }

  public setupPlayer(player: Player, table: Table): void {
    this.events = new EventProxy(this, "target", player.getEventSink());
    this.events.onCollision = (obj: HitObject, ball: Ball, dot: number) => {
      if (!this.data.isDropped) {
        this.animation!.hitEvent = true;
        this.events!.currentHitThreshold = dot;
        obj.fireHitEvent(ball);
      }
    };
    this.events.abortHitTest = () => {
      return this.data.isDropped;
    };
    this.animation = new HitTargetAnimation(this.data, this.state, this.events);
    this.hits = this.hitGenerator.generateHitObjects(this.events, table);
  }

  public getState(): HitTargetState {
    return this.state;
  }

  public getHitShapes(): HitObject[] {
    return this.hits!;
  }

  public getAnimation(): IAnimation {
    return this.animation!;
  }

  /** True when the target is down (a drop target that a ball hit, or one that starts down). */
  public isDropped(): boolean {
    return this.data.isDropped;
  }

  /** Raises a dropped drop target. `timeMs` is the game time. A standup target does nothing. */
  public raise(timeMs: number, table: Table): void {
    if (!this.data.isDropTarget() || !this.data.isDropped || !this.animation) {
      return;
    }
    if (this.animation.moveAnimation && !this.animation.moveDown) {
      return; // the target is already going up: a second call must not restart the delay
    }
    this.animation.moveAnimation = true;
    this.state.zOffset = -HitTarget.DROP_TARGET_LIMIT * table.getScaleZ();
    this.animation.moveDown = false;
    this.animation.timeStamp = timeMs;
  }

  public toFrame(): ItemFrame {
    return this.state.toFrame();
  }
}
